import { FC, KeyboardEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, FormControlLabel, IconButton, Popover, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';

import { BellIcon } from '../../components/Icons';
import BlueIOSSwitch from '../../pages/private/Profile/components/GeneralSettings/BlueIOSSwitch';
import {
  useGetNotificationPreferencesQuery,
  useGetNotificationsQuery,
  useMarkNotificationReadMutation,
  usePatchNotificationPreferencesMutation,
  type NotificationItem,
  type NotificationPreferences,
} from '../../store/services/notificationsApi';
import { useAppSelector } from '../../hooks/redux';
import { useIsMobile } from '../../hooks/useWindowWidth';

import styles from './NotificationsPopover.module.scss';

const PAGE_SIZE = 30;

const PREFERENCE_LABELS: Record<keyof NotificationPreferences, string> = {
  event_reminders: 'Event reminders',
  go_now: 'Go now',
  flight_status: 'Flight status',
  subscription: 'Subscription',
  buddy_invites: 'Buddy invites',
};

function formatNotificationTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return '';
  }
}

const KIND_LABELS: Record<string, string> = {
  event_reminder: 'Event reminder',
  event_start: 'Event start',
  go_now: 'Go now',
  flight_status: 'Flight status',
  subscription: 'Subscription',
  buddy_invite: 'Buddy invite',
  buddy_invites: 'Buddy invite',
};

function formatKindLabel(kind: string): string {
  const k = kind.toLowerCase();
  if (KIND_LABELS[k]) return KIND_LABELS[k];
  return kind.replace(/_/g, ' ');
}

function kindBadgeClass(kind: string): string {
  const k = kind.toLowerCase();
  const map: Record<string, string> = {
    event_reminder: styles.badgeEventReminder,
    event_start: styles.badgeEventStart,
    go_now: styles.badgeGoNow,
    flight_status: styles.badgeFlightStatus,
    subscription: styles.badgeSubscription,
    buddy_invite: styles.badgeBuddyInvite,
  };
  return `${styles.kindBadge} ${map[k] ?? styles.badgeDefault}`;
}

const NotificationsPopover: FC = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const isMobile = useIsMobile();

  const isPremium = useAppSelector(state => state.user.is_premium);

  const visiblePreferenceKeys = useMemo(
    () =>
      (Object.keys(PREFERENCE_LABELS) as (keyof NotificationPreferences)[]).filter(
        key => isPremium || key !== 'flight_status'
      ),
    [isPremium]
  );

  const [page, setPage] = useState(1);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [hasMore, setHasMore] = useState(true);

  const { data: firstPageData, isFetching: firstPageFetching } = useGetNotificationsQuery({
    page: 1,
    page_size: PAGE_SIZE,
  });
  const hasUnread = firstPageData?.results?.some(n => !n.read_at) ?? false;

  const { data, isFetching } = useGetNotificationsQuery({ page, page_size: PAGE_SIZE }, { skip: !open });
  const { data: preferences, isFetching: prefsLoading } = useGetNotificationPreferencesQuery(undefined, {
    skip: !open,
  });
  const [markRead] = useMarkNotificationReadMutation();
  const [patchPreferences, { isLoading: prefsSaving }] = usePatchNotificationPreferencesMutation();
  const [expandedSection, setExpandedSection] = useState<'preferences' | 'list' | null>('list');

  const togglePreferencesSection = () => {
    setExpandedSection(prev => (prev === 'preferences' ? null : 'preferences'));
  };

  const toggleListSection = () => {
    setExpandedSection(prev => (prev === 'list' ? null : 'list'));
  };

  useEffect(() => {
    if (!open) return;

    if (page === 1) {
      if (!firstPageData?.results) return;
      setHasMore(Boolean(firstPageData.next));
      setItems(firstPageData.results);
      return;
    }

    if (!data?.results || isFetching) return;
    setHasMore(Boolean(data.next));
    setItems(prev => {
      const existing = new Set(prev.map(n => n.id));
      const merged = [...prev];
      for (const n of data.results) {
        if (!existing.has(n.id)) merged.push(n);
      }
      return merged;
    });
  }, [open, page, firstPageData, data, isFetching]);

  const handleBellClick = (event: React.MouseEvent<HTMLElement>) => {
    setPage(1);
    setHasMore(true);
    setExpandedSection('list');
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleListScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
      if (nearBottom && hasMore && !isFetching) {
        setPage(p => p + 1);
      }
    },
    [hasMore, isFetching]
  );

  const handleNotificationClick = async (n: NotificationItem) => {
    if (!n.read_at) {
      try {
        await markRead(n.id).unwrap();
        setItems(prev => prev.map(item => (item.id === n.id ? { ...item, read_at: new Date().toISOString() } : item)));
      } catch {
        /* ignore */
      }
    }
  };

  const handlePrefToggle =
    (key: keyof NotificationPreferences) => (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      if (!preferences) return;
      patchPreferences({ [key]: checked });
    };

  const showListSpinner = open && page === 1 && items.length === 0 && firstPageFetching;
  const showFooterSpinner = isFetching && page > 1;

  return (
    <>
      <button
        type="button"
        className={styles.bellButton}
        aria-label="Notifications"
        onClick={handleBellClick}
      >
        <BellIcon
          size={32}
          showUnreadDot={hasUnread}
        />
      </button>
      <Popover
        open={open}
        anchorEl={isMobile ? null : anchorEl}
        anchorReference={isMobile ? 'none' : 'anchorEl'}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        marginThreshold={isMobile ? undefined : null}
        slotProps={{
          paper: {
            className: `${styles.popoverPaper} ${isMobile ? styles.popoverPaperMobile : ''}`,
            elevation: 8,
            sx: isMobile
              ? {
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  zIndex: 14000,
                }
              : {
                  maxHeight: 'min(100vh, 100dvh)',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                },
          },
        }}
        sx={isMobile ? { zIndex: 14000 } : undefined}
      >
        <Box className={`${styles.inner} ${isMobile ? styles.innerMobile : ''}`}>
          <Box className={styles.titleRow}>
            <Typography variant="h7Bold">Notifications</Typography>
            {isMobile ? (
              <IconButton
                onClick={handleClose}
                aria-label="Close notifications"
                className={styles.closeButton}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            ) : null}
          </Box>

          <Box className={styles.settingsBlock}>
            {prefsLoading && !preferences ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={28} />
              </Box>
            ) : preferences ? (
              <>
                <button
                  type="button"
                  className={styles.sectionToggle}
                  aria-expanded={expandedSection === 'preferences'}
                  aria-controls="notification-preferences-panel"
                  id="notification-preferences-heading"
                  onClick={togglePreferencesSection}
                >
                  <Typography
                    variant="subtitle1"
                    component="span"
                    className={styles.sectionToggleLabel}
                  >
                    Notification preferences
                  </Typography>
                  <ExpandMoreIcon
                    className={`${styles.sectionChevron} ${expandedSection === 'preferences' ? styles.sectionChevronOpen : ''}`}
                    aria-hidden
                  />
                </button>
                <Box
                  id="notification-preferences-panel"
                  className={`${styles.prefsAnimWrap} ${expandedSection === 'preferences' ? styles.prefsAnimWrapOpen : ''}`}
                  aria-hidden={expandedSection !== 'preferences'}
                >
                  <Box
                    className={styles.prefsCollapseInner}
                    role="region"
                    aria-labelledby="notification-preferences-heading"
                  >
                    {visiblePreferenceKeys.map(key => (
                      <Box
                        key={key}
                        className={styles.switchWrapper}
                      >
                        <FormControlLabel
                          className={styles.switchRow}
                          control={
                            <BlueIOSSwitch
                              checked={preferences[key]}
                              onChange={handlePrefToggle(key)}
                              disabled={prefsSaving}
                            />
                          }
                          label={<Typography variant="body2">{PREFERENCE_LABELS[key]}</Typography>}
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>
              </>
            ) : null}
          </Box>

          <Box className={`${styles.listBlock} ${expandedSection === 'list' ? styles.listBlockExpanded : ''}`}>
            <button
              type="button"
              className={styles.sectionToggle}
              aria-expanded={expandedSection === 'list'}
              aria-controls="notification-list-panel"
              id="notification-list-heading"
              onClick={toggleListSection}
            >
              <Typography
                variant="subtitle1"
                component="span"
                className={styles.sectionToggleLabel}
              >
                Notification list
              </Typography>
              <ExpandMoreIcon
                className={`${styles.sectionChevron} ${expandedSection === 'list' ? styles.sectionChevronOpen : ''}`}
                aria-hidden
              />
            </button>
            <Box
              id="notification-list-panel"
              className={`${styles.listAnimWrap} ${expandedSection === 'list' ? styles.listAnimWrapOpen : ''}`}
              aria-hidden={expandedSection !== 'list'}
            >
              <Box
                className={styles.list}
                role="region"
                aria-labelledby="notification-list-heading"
                onScroll={handleListScroll}
              >
                {showListSpinner ? (
                  <Box className={styles.footerLoading}>
                    <CircularProgress size={32} />
                  </Box>
                ) : items.length === 0 ? (
                  <Box className={styles.emptyState}>
                    <Typography variant="body2">No notifications yet</Typography>
                  </Box>
                ) : (
                  items.map(n => (
                    <Box
                      key={n.id}
                      className={`${styles.row} ${n.read_at ? styles.rowRead : styles.rowUnread}`}
                      {...(n.read_at
                        ? {}
                        : {
                            onClick: () => handleNotificationClick(n),
                            role: 'button' as const,
                            tabIndex: 0,
                            onKeyDown: (e: KeyboardEvent) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleNotificationClick(n);
                              }
                            },
                          })}
                    >
                      <Box className={styles.rowMeta}>
                        <Typography
                          component="span"
                          variant="caption"
                          className={kindBadgeClass(n.kind)}
                        >
                          {formatKindLabel(n.kind)}
                        </Typography>
                        <Typography
                          variant="caption"
                          className={styles.time}
                        >
                          {formatNotificationTime(n.sent_at || n.created_at)}
                        </Typography>
                      </Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 0.5 }}
                        className={styles.bodyTitle}
                      >
                        {n.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        className={styles.bodyText}
                      >
                        {n.body}
                      </Typography>
                    </Box>
                  ))
                )}
                {showFooterSpinner && (
                  <Box className={styles.footerLoading}>
                    <CircularProgress size={24} />
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default NotificationsPopover;
